import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../../../../API';
import { useFileUpload } from '../../../fileUpload/hooks/useFileUpload';
import { useFileMetadata } from '../../../fileUpload/hooks/useFileMetadata';
import { useCaseCompleteness } from '../../../shared/hooks/useCaseCompleteness';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
    AddSlidesDialog,
    SlideAssignSheet,
    SlideList,
} from '../../slides';
import MoveToStagingModal from '../../slides/MoveToStagingModal';
import { summarizeFilenameMatchResults } from '../../slides/slideStatus';
import slideStyles from '../../slides/slides.module.css';
import styles from '../../CaseBuilderEnhanced.module.css';

function bdsaApproveBody(item) {
    const bdsa = item?.bdsaData || {};
    return {
        bdsa: {
            regions: bdsa.regions || [],
            stain: bdsa.stain || null,
            magnification: bdsa.magnification || '00x',
            level: bdsa.level ?? 1,
            scan: bdsa.scan ?? 1,
        },
    };
}

function itemLabel(item) {
    return item?.name || item?._id || 'Unknown slide';
}

export default function SlidesStep({
    doc,
    setDoc,
    stainSchema,
    networkId,
    affectedItemIds,
}) {
    const queryClient = useQueryClient();
    const completeness = useCaseCompleteness(doc);
    const [addOpen, setAddOpen] = useState(false);
    const [assignItemId, setAssignItemId] = useState(null);
    const [restageItem, setRestageItem] = useState(null);
    const [errorDialog, setErrorDialog] = useState(null);
    const [showBatchConfirm, setShowBatchConfirm] = useState(false);
    const [addMatchSummary, setAddMatchSummary] = useState(null);

    const {
        acceptAndSaveStagedItems,
        acceptStagedLoading,
        uploadLocalFiles,
        uploading,
        uploadProgress,
    } = useFileUpload({ doc, setDoc, networkId });

    const { updateItemMetadata, updateItemMetadataMutation } = useFileMetadata({
        files: {},
        setFiles: () => {},
        doc,
        setDoc,
        networkId,
    });

    const { data: stainResponse } = useQuery({
        queryKey: ['bdsa-stain'],
        queryFn: () => api.get('bdsa/stain'),
        staleTime: 5 * 60 * 1000,
        initialData: stainSchema,
    });
    const stains = stainResponse?.status === 200 && Array.isArray(stainResponse.data)
        ? stainResponse.data
        : (Array.isArray(stainSchema) ? stainSchema : []);

    const refetchCase = async () => {
        queryClient.invalidateQueries({ queryKey: ['bdsa-case', networkId] });
        const response = await api.get('bdsa/case', { query: { networkIdentifier: networkId } });
        if (response?.status === 200 && response?.data) {
            setDoc(response.data);
            return response.data;
        }
        return null;
    };

    const approveMutation = useMutation({
        mutationFn: async ({ itemId, metadata }) => {
            const res = await api.post('network/quarantine/approve', {
                query: { id: itemId },
                body: { metadata: metadata || {} },
            });
            if (res.status !== 200) {
                const msg = (res.data && (res.data.message || res.data)) || 'Approve failed';
                throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
            }
            return res;
        },
    });

    const placeQuarantineMutation = useMutation({
        mutationFn: async ({ itemId }) => {
            const res = await api.post('network/quarantine/set', {
                query: { id: itemId },
            });
            if (res.status !== 200) {
                const msg = (res.data && (res.data.message || res.data)) || 'Failed to place in quarantine';
                throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
            }
            return res;
        },
    });

    const items = doc?.items || [];
    const caseBlocks = useMemo(() => doc?.blocks ?? [], [doc?.blocks]);
    const assignItem = items.find((i) => String(i._id) === String(assignItemId)) || null;
    const batchResults = completeness.batchResults;
    const batchReleaseCount = completeness.releaseReady;

    const showError = (title, message) => {
        setErrorDialog({ title, message: message || 'Something went wrong.' });
    };

    const handleUnquarantine = async (item) => {
        if (!item?._id) return;
        try {
            await approveMutation.mutateAsync({
                itemId: item._id,
                metadata: bdsaApproveBody(item),
            });
            await refetchCase();
        } catch (err) {
            showError(
                "Couldn't release slide",
                `${itemLabel(item)}\n\n${err?.message || 'Release failed.'}`,
            );
        }
    };

    const handlePlaceInQuarantine = async (item) => {
        if (!item?._id) return false;
        try {
            await placeQuarantineMutation.mutateAsync({ itemId: item._id });
            await refetchCase();
            return true;
        } catch (err) {
            showError(
                "Couldn't place slide in quarantine",
                `${itemLabel(item)}\n\n${err?.message || 'Quarantine failed.'}`,
            );
            return false;
        }
    };

    const handleBatchRelease = async () => {
        setShowBatchConfirm(false);
        const toApprove = batchResults.validated;
        if (!toApprove.length) return;
        const results = await Promise.allSettled(toApprove.map((item) => (
            approveMutation.mutateAsync({
                itemId: item._id,
                metadata: bdsaApproveBody(item),
            }).then(() => item)
        )));
        await refetchCase();

        const failures = results
            .map((result, index) => ({ result, item: toApprove[index] }))
            .filter(({ result }) => result.status === 'rejected');
        if (!failures.length) return;

        const detail = failures
            .map(({ result, item }) => `${itemLabel(item)}: ${result.reason?.message || 'Release failed.'}`)
            .join('\n');
        showError(
            failures.length === toApprove.length
                ? "Couldn't release slides"
                : `Couldn't release ${failures.length} of ${toApprove.length} slides`,
            detail,
        );
    };

    return (
        <div className={styles.stepContent}>
            {addMatchSummary && (addMatchSummary.ambiguous > 0 || addMatchSummary.autoAssigned > 0) && (
                <div className={slideStyles.addSummaryBanner} role="status">
                    {addMatchSummary.autoAssigned > 0 && (
                        <span>{addMatchSummary.autoAssigned} slide(s) auto-matched from filename patterns. </span>
                    )}
                    {addMatchSummary.ambiguous > 0 && (
                        <span>{addMatchSummary.ambiguous} slide(s) need block/stain review (ambiguous patterns).</span>
                    )}
                </div>
            )}
            <SlideList
                items={items}
                caseBlocks={caseBlocks}
                affectedItemIds={affectedItemIds}
                onOpenAssign={(item) => setAssignItemId(item._id)}
                onAddSlides={() => setAddOpen(true)}
                addDisabled={!doc?.folderId}
                batchReleaseCount={batchReleaseCount}
                onBatchRelease={() => setShowBatchConfirm(true)}
                batchReleasePending={approveMutation.isPending}
                caseQuarantined={!!doc?.quarantined}
            />

            <AddSlidesDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                acceptAndSaveStagedItems={acceptAndSaveStagedItems}
                acceptStagedLoading={acceptStagedLoading}
                uploadLocalFiles={uploadLocalFiles}
                uploading={uploading}
                uploadProgress={uploadProgress}
                onAfterAdd={(caseDoc) => {
                    const summary = summarizeFilenameMatchResults(caseDoc?.items || []);
                    if (summary.ambiguous > 0 || summary.autoAssigned > 0) {
                        setAddMatchSummary(summary);
                    }
                }}
            />

            <SlideAssignSheet
                open={!!assignItem}
                item={assignItem}
                caseBlocks={caseBlocks}
                caseQuarantined={!!doc?.quarantined}
                stains={stains}
                updateItemMetadata={updateItemMetadata}
                metadataPending={updateItemMetadataMutation.isPending}
                onClose={() => setAssignItemId(null)}
                onUnquarantine={handleUnquarantine}
                unquarantinePending={approveMutation.isPending}
                onPlaceInQuarantine={handlePlaceInQuarantine}
                placeInQuarantinePending={placeQuarantineMutation.isPending}
                onMoveToStaging={(item) => setRestageItem(item)}
            />

            <MoveToStagingModal
                open={!!restageItem}
                item={restageItem}
                onClose={() => setRestageItem(null)}
                onSuccess={async () => {
                    setRestageItem(null);
                    setAssignItemId(null);
                    await refetchCase();
                }}
            />

            <ConfirmDialog
                open={showBatchConfirm}
                title="Release ready slides?"
                message={`This will release ${batchResults.validated.length} ready slide(s). ${batchResults.incomplete.length} incomplete and ${batchResults.duplicates.size} duplicate group(s) will be skipped.`}
                confirmLabel="Release"
                onConfirm={handleBatchRelease}
                onCancel={() => setShowBatchConfirm(false)}
            />

            <ConfirmDialog
                open={!!errorDialog}
                title={errorDialog?.title || 'Error'}
                message={errorDialog?.message || ''}
                confirmLabel="OK"
                hideCancel
                variant="danger"
                onConfirm={() => setErrorDialog(null)}
                onCancel={() => setErrorDialog(null)}
            />
        </div>
    );
}
